<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import { Loader2 } from 'lucide-vue-next'

interface Message {
  id: number
  user_id: number
  sender_name: string
  sender_avatar: string | null
  body: string
  created_at: string
  created_at_iso: string
  is_own: boolean
}

const props = withDefaults(defineProps<{
  messages: Message[]
  loading?: boolean
  hasMore?: boolean
  loadingOlder?: boolean
}>(), {
  hasMore: false,
  loadingOlder: false,
})

const emit = defineEmits<{
  'load-older': []
}>()

const container = ref<HTMLDivElement>()

const scrollToBottom = () => {
  nextTick(() => {
    if (container.value) {
      container.value.scrollTop = container.value.scrollHeight
    }
  })
}

const onScroll = () => {
  if (!container.value || props.loadingOlder || !props.hasMore) return

  // Trigger when scrolled near the top (within 80px)
  if (container.value.scrollTop < 80) {
    emit('load-older')
  }
}

// When older messages are prepended, preserve scroll position
watch(() => props.messages.length, (newLen, oldLen) => {
  if (!container.value) return

  const prepended = newLen > oldLen && props.messages[0]?.id !== container.value.dataset.firstId as any

  if (prepended && container.value.dataset.scrollHeight) {
    // Older messages were prepended — restore scroll position
    nextTick(() => {
      if (container.value) {
        const previousHeight = parseInt(container.value.dataset.scrollHeight || '0')
        container.value.scrollTop = container.value.scrollHeight - previousHeight
      }
    })
  } else {
    // New messages appended — scroll to bottom
    scrollToBottom()
  }
})

// Before loading older, save scroll height so we can restore position
watch(() => props.loadingOlder, (loading) => {
  if (loading && container.value) {
    container.value.dataset.scrollHeight = String(container.value.scrollHeight)
    container.value.dataset.firstId = String(props.messages[0]?.id ?? '')
  }
})

onMounted(scrollToBottom)
</script>

<template>
  <div
    ref="container"
    class="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0 scrollbar-hover"
    @scroll="onScroll"
  >
    <!-- Load older spinner -->
    <div v-if="loadingOlder" class="flex items-center justify-center py-3">
      <Loader2 class="w-5 h-5 text-gray-400 animate-spin" />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="messages.length === 0" class="flex items-center justify-center h-full">
      <div class="text-center">
        <p class="text-sm text-gray-500">No messages yet</p>
        <p class="text-xs text-gray-400 mt-1">Be the first to send a message!</p>
      </div>
    </div>

    <!-- Messages -->
    <template v-else>
      <div
        v-for="message in messages"
        :key="message.id"
        class="flex gap-2.5"
        :class="message.is_own ? 'justify-end' : 'justify-start'"
      >
        <!-- Other user avatar -->
        <Avatar v-if="!message.is_own" class="w-8 h-8 shrink-0 mt-0.5">
          <AvatarImage :src="message.sender_avatar" />
          <AvatarFallback />
        </Avatar>

        <div
          class="max-w-[75%] rounded-2xl px-3.5 py-2"
          :class="message.is_own
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-bl-md'"
        >
          <p v-if="!message.is_own" class="text-xs font-medium mb-0.5" :class="message.is_own ? 'text-blue-100' : 'text-gray-600'">
            {{ message.sender_name }}
          </p>
          <p class="text-sm whitespace-pre-wrap break-words">{{ message.body }}</p>
          <p class="text-[10px] mt-1" :class="message.is_own ? 'text-blue-200' : 'text-gray-400'">
            {{ message.created_at }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
